import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"

// Lead Replacement Policy agreement gate (lrp_agreements/new). Two gates in a
// row: the consent checkbox is locked until the policy modal has been closed
// once, and the Submit is locked until that checkbox is checked. The server
// re-checks consent on POST regardless (LrpAgreementsController#create).
//
// A11Y-1 / BUTTON-4 (#13355): every disabled frame of the Submit says which
// gate holds it — as the button's own gl-tooltip plus a "<label> — <reason>"
// accessible name (lib/gate_reason) — and both are withdrawn the moment the
// button is enabled. A valid submission that is leaving the page holds the
// button with the in-flight reason so a second click cannot post twice.
export default class extends Controller {
  // The three reasons are authored ONCE, in lrp_agreements/new.html.haml,
  // which paints the first (review) reason server-side on the disabled button
  // and stamps all three here as values — so the first paint and every later
  // gate change say the same thing. The defaults only serve a DOM that stamps
  // nothing (the Jest fixtures).
  static values = {
    reviewReason: { type: String, default: "Review the updated policy first" },
    agreeReason: { type: String, default: "Check the agreement box first" },
    submittingReason: { type: String, default: "Submitting your agreement…" }
  }

  connect() {
    this.inFlight = false
    this.initializeElements()
    this.setupEventListeners()
    this.syncSubmit()
  }

  disconnect() {
    if (this.policyModal && this._onModalHidden) {
      this.policyModal.removeEventListener('hidden.bs.modal', this._onModalHidden)
    }
    if (this.agreedCheckbox && this._onCheckboxChange) {
      this.agreedCheckbox.removeEventListener('change', this._onCheckboxChange)
    }
    if (this.form && this._onSubmit) {
      this.form.removeEventListener('submit', this._onSubmit)
    }
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow)
    }
  }

  initializeElements() {
    this.agreedCheckbox = document.getElementById('lrp_agreement_agreed')
    this.submitButton = document.getElementById('submit-agreement')
    this.reviewPolicyLink = document.getElementById('review-policy-link')
    this.form = document.querySelector('.lrp-form')
    this.policyModal = document.getElementById('lrpPolicyModal')
  }

  setupEventListeners() {
    if (!this.agreedCheckbox || !this.submitButton || !this.form) return

    if (this.policyModal) {
      this._onModalHidden = () => {
        if (this.agreedCheckbox) {
          this.agreedCheckbox.disabled = false
        }
        this.syncSubmit()
      }
      this.policyModal.addEventListener('hidden.bs.modal', this._onModalHidden)
    }

    this._onCheckboxChange = () => this.syncSubmit()
    this.agreedCheckbox.addEventListener('change', this._onCheckboxChange)

    this._onSubmit = (event) => this.handleSubmit(event)
    this.form.addEventListener('submit', this._onSubmit)

    // A back/forward-cache restore brings the page back with the in-flight
    // frame still painted and no controller reconnect; settle to the real gate.
    this._onPageShow = (event) => {
      if (event.persisted) this.settle()
    }
    window.addEventListener('pageshow', this._onPageShow)
  }

  handleSubmit(event) {
    if (!this.form.checkValidity()) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.agreedCheckbox.checked) {
      event.preventDefault()
      if (typeof window.showToast === 'function') {
        window.showToast('You must agree to the Lead Replacement Policy to continue.', 'bg-warning')
      }
      return false
    }

    this.form.classList.add('was-validated')
    if (event.defaultPrevented) return false

    // The submission is leaving: hold the button with the in-flight reason so
    // a second click (or Enter) cannot record the agreement twice. Disabling
    // the submitter inside its own submit event never cancels the submission.
    this.inFlight = true
    this.syncSubmit()
    return true
  }

  // The request is over and the page is still here (bfcache restore): the
  // button's state is whatever the gates say NOW, never a blind re-enable.
  settle() {
    this.inFlight = false
    this.syncSubmit()
  }

  // The Submit's state and reason, from one place: in flight while a valid
  // submission is leaving, the review gate while the checkbox is still locked,
  // the checkbox gate while it is unchecked, enabled otherwise. The reason is
  // applied as the button's gl-tooltip plus its "<label> — <reason>" name and
  // both are withdrawn when it is enabled (lib/gate_reason).
  syncSubmit() {
    if (!this.submitButton) return
    syncGateReason(this.submitButton, { ready: this.ready(), reason: this.disabledReason() })
  }

  reviewed() {
    return !!this.agreedCheckbox && !this.agreedCheckbox.disabled
  }

  ready() {
    return !this.inFlight && this.reviewed() && this.agreedCheckbox.checked
  }

  disabledReason() {
    if (this.inFlight) return this.submittingReasonValue
    if (!this.reviewed()) return this.reviewReasonValue
    return this.agreeReasonValue
  }
}
